import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'

const HAZARD_TYPES = [
  { id: 'job_demands', label: 'Excessive Job Demands', regulation: 'WHS Reg 2025 (NSW) s55A' },
  { id: 'low_control', label: 'Low Job Control', regulation: 'NSW Psychosocial Code of Practice 2022' },
  { id: 'poor_support', label: 'Poor Support', regulation: 'NSW Psychosocial Code of Practice 2022' },
  { id: 'role_conflict', label: 'Role Conflict or Ambiguity', regulation: '' },
  { id: 'poor_relationships', label: 'Poor Workplace Relationships', regulation: '' },
  { id: 'bullying', label: 'Bullying or Harassment', regulation: 'WHS Act s19, Fair Work Act' },
  { id: 'violence', label: 'Workplace Violence or Aggression', regulation: 'Notifiable from 2025' },
  { id: 'remote_work', label: 'Remote or Isolated Work Risks', regulation: '' },
  { id: 'fatigue', label: 'Fatigue from Work Design', regulation: 'Fatigue CoP Nov 2025' },
  { id: 'change', label: 'Poor Organisational Change Management', regulation: '' },
  { id: 'low_recognition', label: 'Low Recognition and Reward', regulation: '' },
  { id: 'trauma', label: 'Exposure to Traumatic Events', regulation: '' },
]

export default function PsychosocialNew() {
  const navigate = useNavigate()
  const [form, setForm] = useState({
    hazard_type: '', description: '', department: '', source: 'observation',
    risk_rating: '', controls: [] as string[], confidential: false
  })
  const [newControl, setNewControl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      const { data } = await api.post('/psychosocial', form)
      navigate('/psychosocial')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit')
    }
    setLoading(false)
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Report Psychosocial Hazard</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card space-y-4">
          <div>
            <label>Hazard Type *</label>
            <select value={form.hazard_type} onChange={e => setForm(f => ({ ...f, hazard_type: e.target.value }))} required>
              <option value="">Select hazard type...</option>
              {HAZARD_TYPES.map(h => (
                <option key={h.id} value={h.id}>{h.label}{h.regulation ? ` (${h.regulation})` : ''}</option>
              ))}
            </select>
          </div>

          <div>
            <label>Description *</label>
            <textarea rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required placeholder="Describe the psychosocial hazard..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label>Department</label>
              <input value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} placeholder="e.g. Operations" />
            </div>
            <div>
              <label>Source</label>
              <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}>
                <option value="observation">Observation</option>
                <option value="survey">Survey</option>
                <option value="complaint">Complaint</option>
                <option value="incident">Incident</option>
                <option value="audit">Audit</option>
              </select>
            </div>
          </div>

          <div>
            <label>Risk Rating</label>
            <select value={form.risk_rating} onChange={e => setForm(f => ({ ...f, risk_rating: e.target.value }))}>
              <option value="">Select...</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label>Controls</label>
            <div className="flex gap-2 mb-2">
              <input value={newControl} onChange={e => setNewControl(e.target.value)} placeholder="Add a control measure..." />
              <button type="button" className="btn btn-secondary flex-shrink-0" onClick={() => { if (newControl) { setForm(f => ({ ...f, controls: [...f.controls, newControl] })); setNewControl('') }}}>Add</button>
            </div>
            {form.controls.map((c, i) => (
              <div key={i} className="badge badge-low mb-1 mr-1 cursor-pointer" onClick={() => setForm(f => ({ ...f, controls: f.controls.filter((_, j) => j !== i) }))}>{c} ×</div>
            ))}
          </div>

          <label className="flex items-center gap-2 cursor-pointer m-0 font-normal">
            <input type="checkbox" checked={form.confidential} onChange={e => setForm(f => ({ ...f, confidential: e.target.checked }))} className="w-4 h-4" style={{ width: '1rem' }} />
            <span className="text-sm" style={{ color: 'var(--text)' }}>Confidential report (only admins and you can view)</span>
          </label>
        </div>

        {error && <div className="text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>{error}</div>}

        <div className="flex gap-3">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Submitting...' : 'Report Hazard'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/psychosocial')}>Cancel</button>
        </div>
      </form>
    </div>
  )
}
